import React from 'react';
import { graphql, navigate } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';

import { SEO } from './../components';
import InteractiveTabs from '@src/components/interactive-tabs';
import VideoPlayer from 'components/VideoPlayer';
import { addToCart } from '../store/cart';

import {
  PlayGameTab,
  MoreInfoTab,
  DocumentationTab,
  InteractiveLearningIntro,
  useDocumentsPanel,
  useFullscreen,
  buildIframeQueryString,
  collectDocumentsFromGameCards,
  collectVideoSources,
} from '../components/InteractiveLearning';

const DOCUMENTS_PANEL_STYLES = `
  .documents-panel-desktop,
  #documents-dropdown-panel {
    display: block;
  }

  /* Button is in DOM with id="documents-button" but visually hidden so iframe can trigger it by ID.
     From iframe (same-origin only): window.parent.document.getElementById('documents-button')?.click() */
  #documents-button.documents-button-hidden {
    position: absolute !important;
    left: -9999px !important;
    top: 0 !important;
    width: 1px !important;
    height: 1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
    line-height: 10px !important;
  }
  
  
  }

  #documents-dropdown-panel::-webkit-scrollbar {
    width: 8px;
  }
  #documents-dropdown-panel::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  #documents-dropdown-panel::-webkit-scrollbar-thumb {
    background: #FD99C5;
    border-radius: 4px;
  }
  #documents-dropdown-panel::-webkit-scrollbar-thumb:hover {
    background: #FC7BB5;
  }
`;

/**
 * @param {{
 *    data: {
 *      interactiveLearningPage: {
 *        description: string
 *        title: string
 *        horizontalPdf: boolean
 *        topYoutubeVideo?: string
 *        topVideo?: {
 *          video: {
 *              streamingUrl: string
 *           }
 *        },
 *        pdfLink?: string
 *        subscriptionOnly: boolean
 *        downloadablePdf: boolean
 *        video?: {
 *          video: {
 *              streamingUrl: string
 *           }
 *        },
 *        gameCards?: {
 *          slug?: string
 *          youtubeVideo?: string
 *          cards?: {
 *            description?: string
 *            id?: string
 *            title?: string
 *          }[]
 *        }[]
 *        videos?: {
 *          video: {
 *              streamingUrl: string
 *          }
 *        }[]
 *        youtubeVideo?: string
 *        paragraphs?: {
 *          paragraph: string
 *          audioClip?: {
 *            url?: string
 *          }
 *          buttonLabel?: string
 *        }[]
 *        seo?: {
 *          title: string
 *          description: string
 *          image?: {
 *              url: string
 *          }
 *        }
 *        seoKeywords?: {
 *          keyword: string
 *        }[],
 *        schemaMarkup: string
 *      }
 *    }
 * }} param
 */
export default function InteractiveLearning({ data }) {
  const page = data.interactiveLearningPage;
  const {
    description,
    title,
    topVideo,
    topYoutubeVideo,
    useTabbedTemplate,
    iframeLink,
    pdfLink,
    horizontalPdf,
    content,
    seo,
    seoKeywords,
    schemaMarkup,
    subscriptionOnly,
    video,
    videos,
    youtubeVideo,
    downloadablePdf,
    gameCards,
  } = page;

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);

  const isSubscribed = user?.subscribed ?? false;
  const miniCart = useSelector((state) => new Set(state.cart.miniCart));
  const ownedProducts = useSelector((state) => new Set(state.cart.ownedProducts));

  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { showPanel, showButton, togglePanel } = useDocumentsPanel();

  const handleAddToCart = (documentId) => {
    dispatch(addToCart(documentId, user));
  };

  const iframeQueryString = buildIframeQueryString(gameCards);
  const { cheatSheets: allCheatSheetDocuments, worksheets: allWorkSheetDocuments } =
    collectDocumentsFromGameCards(gameCards);
  const allVideos = collectVideoSources({ video, youtubeVideo, videos });

  React.useEffect(() => {
    if (subscriptionOnly && !isSubscribed) {
      navigate('/interactive-learning');
    }
  }, [subscriptionOnly, isSubscribed, user]);

  const tabs = [
    ...(iframeLink
      ? [
          {
            id: 'play',
            title: 'Play Game',
            contained: false,
            content: (
              <PlayGameTab
                iframeLink={iframeLink}
                title={title}
                queryString={iframeQueryString}
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
                showDocumentsButton={showButton}
                showDocumentsPanel={showPanel}
                onToggleDocumentsPanel={togglePanel}
                cheatSheetDocuments={allCheatSheetDocuments}
                workSheetDocuments={allWorkSheetDocuments}
                isSubscribed={isSubscribed}
                isLoggedIn={!!user?.email}
                subscriptionPrice={undefined}
                miniCart={miniCart}
                ownedProducts={ownedProducts}
                onAddToCart={handleAddToCart}
                onSubscribe={() => navigate('/subscriptions')}
              />
            ),
          },
        ]
      : []),
    ...(content?.length > 0
      ? [
          {
            id: 'more-info',
            title: 'More Information',
            contained: true,
            content: <MoreInfoTab content={content} />,
          },
        ]
      : []),
    ...(pdfLink
      ? [
          {
            id: 'documentation',
            title: 'Download / View Notes',
            contained: true,
            content: (
              <DocumentationTab
                pdfLink={pdfLink}
                title={title}
                downloadablePdf={downloadablePdf}
                horizontalPdf={horizontalPdf}
              />
            ),
          },
        ]
      : []),
  ].filter(Boolean);

  const [activeTab, setActiveTab] = React.useState(tabs[0]?.id);
  const showTabs = useTabbedTemplate;

  return (
    <>
      <style>{DOCUMENTS_PANEL_STYLES}</style>

      <SEO
        description={seo?.description}
        image={seo?.image}
        title={seo?.title ?? `Interactive Learning-${title}`}
        seoKeywords={seoKeywords}
        schema={schemaMarkup ? JSON.parse(schemaMarkup) : undefined}
      />

      <InteractiveLearningIntro
        title={title}
        description={description}
        topVideo={topVideo}
        topYoutubeVideo={topYoutubeVideo}
      />

      {allVideos.length > 0 && (
        <div className="container content-section mb-2 pt-0 brainteaser-container">
          {allVideos.map((src) => (
            <VideoPlayer key={src} src={src} />
          ))}
        </div>
      )}

      {showTabs ? (
        <InteractiveTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      ) : (
        <div className="container content-section md:px-0 px-1 pt-0 mb-5 brainteaser-container">
          {tabs.map((tab) => (
            <div key={tab.id} style={{ marginBottom: '1.5rem' }}>
              {tab.content}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export const query = graphql`
  query InteractiveLearningPageQuery($id: String!) {
    interactiveLearningPage: datoCmsInteractiveLearningPage(originalId: { eq: $id }) {
      title
      iframeLink
      useTabbedTemplate
      pdfLink
      horizontalPdf
      topVideo {
        video {
          streamingUrl
        }
      }
      gameCards {
        slug
        youtubeVideo
        cards {
          description
          id
          title
        }
        cheatSheetDocuments {
          displayName
          fileUrl
          hasPassword
          hidePreview
          previewUrl
          name
          originalId
          freeDocument
          freeSample
        }
        workSheetDocuments {
          fileUrl
          hasPassword
          hidePreview
          previewUrl
          price
          tableTooltip
          name
          originalId
          freeDocument
          freeSample
        }
      }
      topYoutubeVideo
      description
      subscriptionOnly
      youtubeVideo
      video {
        video {
          streamingUrl
        }
      }
      videos {
        video {
          streamingUrl
        }
      }
      downloadablePdf
      content {
        body {
          blocks {
            __typename
            ... on DatoCmsVideo {
              id
              originalId
              video {
                url
              }
            }
            ... on DatoCmsImageBlock {
              id
              originalId
              image {
                url
                alt
              }
            }
            ... on DatoCmsAudioPlayer {
              id
              originalId
              title
              audio {
                url
              }
              audioUrl
            }
            ... on DatoCmsTinymce {
              id
              originalId
              body
            }
            ... on DatoCmsImageGallery {
              id
              originalId
              images {
                alt
                url
              }
              columns
            }
          }
          value
        }
      }
      paragraphs {
        audioClip {
          url
        }
        paragraph
        buttonLabel
      }
      seo {
        description
        image {
          url
        }
        title
      }
      seoKeywords {
        keyword
      }
      schemaMarkup
    }
  }
`;
